import React, { useCallback, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { Button, DeleteButton } from '../util/buttons';
import SelectProjectsPopup from '@components/shared/rnd_report/SelectProjectsPopup';
import EditDynamicFieldsModal from '@components/shared/rnd_report/EditDynamicFieldsModal';
import { Table } from '../util';
import { createTimezonedDate } from '../util/date';
import { Loader } from '../util/Loader';
import { useGetUserClaimGroupAbilities } from '../util/userAbilities';
import { useSelector } from 'react-redux';

// relevant for claim group rnd reports
const reportsTableStyle = {
  maxHeight: '200px',
  overflowY: 'auto'
};
export default function ReportPreviewControls(_ref) {
  let {
    accountType,
    claimGroup,
    claimGroupRndReportTemplate,
    changeClaimId,
    shouldShowComments,
    setShouldShowComments,
    reportType,
    isUnderReview,
    aReportGenerationIsPending,
    generateReport,
    isGenerateButtonDisabled,
    nonRndReportSnapshots = [],
    getRndReportDownloadLink,
    deleteRndReport
  } = _ref;
  const [showEditDynamicFieldsModal, setShowEditDynamicFieldsModal] = useState(false);
  // only relevant for claim group rnd reports
  const [showclaimIdSelect, setShowClaimIdSelect] = useState(false);
  const isPdfGenerationPendingForRndReport = useCallback(report => ['pending', 'not_started'].includes(report.pdf_generation_status), []);
  const user = useSelector(state => state.auth.user);
  const userClaimGroupAbilities = window.MARKET_SETUP.useGetUserClaimGroupAbilities({
    user,
    claimGroup
  });
  const pageAccess = userClaimGroupAbilities?.pages?.ProjectRndReportsPage;
  const renderDownloadLink = useCallback(report => {
    return /*#__PURE__*/React.createElement("small", null, /*#__PURE__*/React.createElement("a", {
      disabled: isPdfGenerationPendingForRndReport(report),
      href: getRndReportDownloadLink(report),
      target: "_blank",
      rel: "noreferrer",
      download: true
    }, /*#__PURE__*/React.createElement("span", {
      className: "fa fa-download"
    }), " Download report"));
  }, [getRndReportDownloadLink, isPdfGenerationPendingForRndReport]);
  const getReportsTableRows = useCallback(() => {
    const rows = nonRndReportSnapshots.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(r => ({
      key: r.id,
      columns: [createTimezonedDate(new Date(r.created_at)).toCustomLocaleTimeString(), r.author_name, r.pdf_generation_status, isPdfGenerationPendingForRndReport(r) ? /*#__PURE__*/React.createElement(Loader, null) : renderDownloadLink(r), /*#__PURE__*/React.createElement(DeleteButton, {
        disabled: isPdfGenerationPendingForRndReport(r),
        onClick: isPdfGenerationPendingForRndReport(r) ? () => {} : () => deleteRndReport(r)
      })]
    }));
    return rows;
  }, [deleteRndReport, isPdfGenerationPendingForRndReport, nonRndReportSnapshots, renderDownloadLink]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    className: "shadow border-0"
  }, /*#__PURE__*/React.createElement(Card.Header, {
    className: "bg-primary bg-gradient text-white"
  }, "Reports"), /*#__PURE__*/React.createElement(Card.Body, null, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    md: {
      span: 2
    }
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: isGenerateButtonDisabled,
    loading: aReportGenerationIsPending,
    onClick: () => generateReport('final'),
    variant: isUnderReview ? 'dark' : 'primary'
  }, isUnderReview ? 'PREVIEW PDF REPORT WITH ACCEPTED CHANGES' : 'GENERATE PDF REPORT')), /*#__PURE__*/React.createElement(Col, {
    md: {
      span: 2,
      offset: 8
    }
  }, pageAccess.components.ReportPreviewControls.canEditDynamicFields && /*#__PURE__*/React.createElement(Button, {
    style: {
      width: '100%'
    },
    disabled: aReportGenerationIsPending,
    loading: aReportGenerationIsPending,
    onClick: () => setShowEditDynamicFieldsModal(true)
  }, "Edit Dynamic Fields"))), /*#__PURE__*/React.createElement(Row, {
    className: "mt-3"
  }, isUnderReview && /*#__PURE__*/React.createElement(Col, {
    md: {
      span: 3
    }
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: isGenerateButtonDisabled,
    loading: aReportGenerationIsPending,
    onClick: () => generateReport('current'),
    variant: 'dark'
  }, "PREVIEW PDF WITH HIGLIGHTS")), claimGroupRndReportTemplate && /*#__PURE__*/React.createElement(Col, {
    md: {
      span: 2,
      offset: isUnderReview ? 7 : 10
    }
  }, /*#__PURE__*/React.createElement(SelectProjectsPopup, {
    style: {
      width: '100%'
    },
    claimGroup: claimGroupRndReportTemplate.claim_group,
    disabled: aReportGenerationIsPending,
    loading: aReportGenerationIsPending,
    accountType: accountType
  }))), claimGroupRndReportTemplate && reportType === 'aif' && /*#__PURE__*/React.createElement(Row, {
    className: "mt-3"
  }, /*#__PURE__*/React.createElement(Col, {
    md: {
      span: 2,
      offset: 10
    }
  }, /*#__PURE__*/React.createElement(Button, {
    style: {
      width: '100%'
    },
    disabled: aReportGenerationIsPending,
    loading: aReportGenerationIsPending,
    onClick: () => setShowClaimIdSelect(true)
  }, "Select Year"), showclaimIdSelect && /*#__PURE__*/React.createElement(Form.Control, {
    className: "mt-1",
    type: "text",
    as: "select",
    name: "claim_year",
    onChange: changeClaimId
  }, /*#__PURE__*/React.createElement("option", {
    value: null
  }), claimGroupRndReportTemplate.claims.map((claim, i) => /*#__PURE__*/React.createElement("option", {
    key: claim.id,
    value: claim.id
  }, claim.name))))), isUnderReview && /*#__PURE__*/React.createElement(Row, {
    className: "mt-3"
  }, /*#__PURE__*/React.createElement(Col, {
    md: 6
  }, /*#__PURE__*/React.createElement(Form.Check, {
    checked: shouldShowComments,
    label: "Show comments in report?",
    onChange: e => setShouldShowComments(e.target.checked)
  }))), /*#__PURE__*/React.createElement(Row, {
    style: reportsTableStyle,
    className: "pt-4"
  }, /*#__PURE__*/React.createElement(Col, {
    md: 12,
    className: "text-center"
  }, /*#__PURE__*/React.createElement(Table, {
    headers: [],
    rows: getReportsTableRows(),
    showControls: false
  }))))), claimGroupRndReportTemplate && showEditDynamicFieldsModal && /*#__PURE__*/React.createElement(EditDynamicFieldsModal, {
    accountType: accountType,
    claimGroup: claimGroupRndReportTemplate.claim_group,
    show: showEditDynamicFieldsModal,
    onHide: () => setShowEditDynamicFieldsModal(false)
  }));
}